import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  counterpartyDisplayName,
  counterpartyTypeAbbrev,
  type UnlinkRequestBannerData,
} from "./facilityNetworkUnlinkRequestUtils";
import RequestDialogFacilityCard from "./RequestDialogFacilityCard";
import {
  requestDialogBodySx,
  requestDialogFooterSx,
  requestDialogHeaderSx,
  requestDialogPaperSx,
  requestDialogShellSx,
} from "./requestDialogLayout";
import { FN } from "./facilityNetworkTheme";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import chevronWhite from "@assets/facilityNetwork/req-chevron-white.svg";

type Bullet = { text: string; tone: "teal" | "red" };

/** Child viewing parent-initiated unlink (Figma 9131:97591 copy). */
const POINTS_CHILD: Bullet[] = [
  {
    text: "Your facility will be removed from their network and oversight dashboard",
    tone: "red",
  },
  {
    text: "The parent facility will lose visibility into your compliance status immediately",
    tone: "red",
  },
  {
    text: "Your facility data and settings are unaffected — nothing changes on your end",
    tone: "teal",
  },
  {
    text: "You can re-link to this or another parent at any time",
    tone: "teal",
  },
];

/** Parent viewing child-initiated unlink. */
const POINTS_PARENT: Bullet[] = [
  {
    text: "The child facility will be removed from your network and oversight dashboard",
    tone: "red",
  },
  {
    text: "You will lose visibility into their compliance status immediately",
    tone: "red",
  },
  {
    text: "Their facility data and settings are unaffected — nothing is deleted",
    tone: "teal",
  },
  {
    text: "They can request to re-link from their facility network page at any time",
    tone: "teal",
  },
];

type Props = {
  open: boolean;
  request: UnlinkRequestBannerData | null;
  submitting?: boolean;
  onClose: () => void;
  onDecline: (reason: string) => void;
  onAccept: () => void;
};

/** Figma 9131:97591 — Review unlink request on banner Review click */
export default function UnlinkRequestDialog({
  open,
  request,
  submitting = false,
  onClose,
  onDecline,
  onAccept,
}: Props) {
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!open) {
      setIsRejecting(false);
      setRejectReason("");
    }
  }, [open, request?.requestId]);

  if (!request) return null;

  const name = counterpartyDisplayName(request);
  const typeAbbrev = counterpartyTypeAbbrev(request);
  const points = request.initiatorRole === "child" ? POINTS_PARENT : POINTS_CHILD;
  const metaItems = [
    { label: "CCN", value: request.ccn?.trim() || "NA" },
    { label: "NPI", value: request.npi?.trim() || "NA" },
    { label: "Type", value: typeAbbrev },
    ...(request.state ? [{ label: "State", value: request.state }] : []),
  ];
  const reasonText =
    request.reason?.trim() ||
    "Facility is being reassigned to another parent network";

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: requestDialogPaperSx }}
    >
      <Box sx={requestDialogShellSx}>
        <Box sx={requestDialogHeaderSx}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: "#242424",
              lineHeight: "20px",
            }}
          >
            Unlink request
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            sx={{ borderRadius: "4px", p: "2px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={requestDialogBodySx}>
          <RequestDialogFacilityCard
            sectionLabel="Requesting to unlink"
            facilityName={name}
            metaItems={metaItems}
            tone="error"
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: "#242424",
                lineHeight: "20px",
              }}
            >
              Reason provided
            </Typography>
            <Box
              sx={{
                borderLeft: "1px solid #e7a3a7",
                borderRadius: "5px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#fafafa",
                  border: "1px solid #e5e5e5",
                  borderRadius: "5px",
                  p: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.figmaAccent,
                    letterSpacing: "0.03px",
                    lineHeight: "16px",
                  }}
                >
                  {reasonText}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", py: 2 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: "#242424",
                lineHeight: "20px",
                mb: 0.25,
              }}
            >
              What accepting means
            </Typography>
            {points.map((point) => (
              <Box
                key={point.text}
                sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "24px",
                    bgcolor: point.tone === "red" ? "#d13438" : FN.primary,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.figmaAccent,
                    letterSpacing: "0.03px",
                    lineHeight: "16px",
                    flex: 1,
                  }}
                >
                  {point.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {isRejecting ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#242424",
                  lineHeight: "20px",
                }}
              >
                Reason for rejection{" "}
                <Box component="span" sx={{ color: FN.figmaError }}>
                  *
                </Box>
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why you are rejecting this unlink..."
                disabled={submitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fafafa",
                    borderRadius: "5px",
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    "& fieldset": { borderColor: "#e5e5e5" },
                  },
                }}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={requestDialogFooterSx}>
          <Button
            variant="outlined"
            disabled={submitting}
            onClick={() => {
              if (isRejecting) {
                setIsRejecting(false);
                setRejectReason("");
                return;
              }
              onClose();
            }}
            sx={{
              textTransform: "none",
              height: 32,
              minHeight: 32,
              px: 1.5,
              borderRadius: "4px",
              borderColor: "#d4d4d4",
              color: FN.text,
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#fff",
            }}
          >
            Cancel
          </Button>
          {!isRejecting ? (
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() => setIsRejecting(true)}
              sx={{
                textTransform: "none",
                height: 32,
                minHeight: 32,
                px: 1.5,
                borderRadius: "4px",
                borderColor: FN.figmaErrorStroke,
                color: FN.figmaError,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                bgcolor: "#fff",
              }}
            >
              Reject
            </Button>
          ) : (
            <Button
              variant="outlined"
              disabled={submitting || !rejectReason.trim()}
              onClick={() => onDecline(rejectReason.trim())}
              sx={{
                textTransform: "none",
                height: 32,
                minHeight: 32,
                px: 1.5,
                borderRadius: "4px",
                borderColor: FN.figmaErrorStroke,
                color: FN.figmaError,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                bgcolor: "#fff",
              }}
            >
              Confirm rejection
            </Button>
          )}
          {!isRejecting ? (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={onAccept}
              endIcon={
                submitting ? (
                  <CircularProgress size={12} sx={{ color: "#fff" }} />
                ) : (
                  <FacilityNetworkFigmaIcon src={chevronWhite} size={12} />
                )
              }
              sx={{
                textTransform: "none",
                height: 32,
                minHeight: 32,
                px: 2,
                borderRadius: "4px",
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: "#fff",
                background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
                boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
                "&:hover": {
                  background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)",
                },
                "& .MuiButton-endIcon": { ml: 1 },
              }}
            >
              Approve Unlink
            </Button>
          ) : null}
        </Box>
      </Box>
    </Dialog>
  );
}
